import { useFormContext, Controller, type FieldValues } from 'react-hook-form';
import { CheckboxGroup } from 'pink-lava-ui';
import type { roundedType } from 'pink-lava-ui/types';

export interface CheckboxOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

interface CheckboxInputProps<T extends FieldValues> {
  name: (keyof T & string) | '';
  label?: string;
  disabled?: boolean;
  required?: boolean;
  showErrorMessage?: boolean;
  onChange?: (_value: any, _fieldOnChange: (_value: any) => void) => void;
  onChangeCallback?: (_value: any, _setValue: (_name: keyof T & string, _value: any) => void) => void;
  validate?: (_value: any) => boolean | string;
  options: CheckboxOption[];
  size?: 'small' | 'middle' | 'large';
  className?: string;
  rounded?: roundedType;
  direction?: 'horizontal' | 'vertical';
  info?: string;
  /** Value that is mutually exclusive with other options (e.g., "tidak_ada_kendala") */
  mutuallyExclusiveValue?: string | number;
}

export const CheckboxInput = <T extends FieldValues>({
  name,
  label,
  disabled = false,
  required = false,
  showErrorMessage = true,
  onChange,
  onChangeCallback,
  validate,
  options = [],
  size = 'middle',
  className = '',
  rounded,
  info,
  mutuallyExclusiveValue,
}: CheckboxInputProps<T>) => {
  const { control, setValue, watch } = useFormContext();

  const checkboxClassName = `w-full ${rounded === 'large' ? 'rounded-full' : rounded ? 'rounded-lg' : ''} ${className}`;

  // Watch the field value to compute disabled options
  const currentValue = watch(name) || [];

  // Compute options with mutually exclusive logic
  const computedOptions = !mutuallyExclusiveValue
    ? options
    : options.map(option => {
        const hasExclusiveValue = currentValue.includes(mutuallyExclusiveValue);
        const hasOtherValues = currentValue.some((v: any) => v !== mutuallyExclusiveValue);

        // If this is the mutually exclusive option
        if (option.value === mutuallyExclusiveValue) {
          // Disable it if any other values are selected
          return {
            ...option,
            disabled: option.disabled || disabled || hasOtherValues,
          };
        }
        // For other options, disable them if the exclusive value is selected
        return {
          ...option,
          disabled: option.disabled || disabled || hasExclusiveValue,
        };
      });

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required ? `${label || 'This field'} is required` : false,
        validate: validate,
      }}
      render={({ field, fieldState: { error } }) => (
        <div className="w-full">
          {label && (
            <label className="block text-sm font-medium text-gray-700 mb-1 font-nunito">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          <CheckboxGroup
            value={field.value || []}
            disabled={disabled}
            onChange={value => {
              let newValue = value;

              // Handle mutually exclusive logic
              if (mutuallyExclusiveValue) {
                const isSelectingExclusive = value.includes(mutuallyExclusiveValue);
                const wasSelectingExclusive = (field.value || []).includes(mutuallyExclusiveValue);

                // If selecting the exclusive value, clear all other values
                if (isSelectingExclusive && !wasSelectingExclusive) {
                  newValue = [mutuallyExclusiveValue];
                }
                // If selecting any other value while exclusive is selected, remove exclusive value
                else if (!isSelectingExclusive && wasSelectingExclusive) {
                  newValue = value;
                }
              }

              if (onChange) {
                // Scenario 1: onChange overrides default behavior
                onChange(newValue, field.onChange);
              } else {
                // Default behavior: update form field
                field.onChange(newValue);
                // Scenario 2: onChangeCallback executes alongside default behavior
                onChangeCallback?.(newValue, setValue);
              }
            }}
            options={computedOptions}
            size={size}
            orientation="vertical"
            className={checkboxClassName}
          />
          {info && <span className="text-gray-500 text-xs mt-1 block">{info}</span>}
          {showErrorMessage && error && <span className="text-red-500 text-xs mt-1 block">{error.message}</span>}
        </div>
      )}
    />
  );
};
